import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';

const COURSE_URL = "/course";

export default function DiagnosticPage() {
  const [stage, setStage] = useState('intro'); // intro, questions, gate, result
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [formData, setFormData] = useState({ firstName: '', email: '' });
  const [result, setResult] = useState(null);

  const questions = [
    {
      id: 1,
      domain: "Level 01 — Competence",
      text: "When something important is coming, what do you reach for first?",
      options: [
        { text: "More preparation. Read more, model more, know more.", value: "c" },
        { text: "A rehearsal. I want to have done it once before it counts.", value: "f" },
        { text: "Quiet. I need space before I need information.", value: "p" },
        { text: "I go in. Preparation past a point makes me worse.", value: "f" }
      ]
    },
    {
      id: 2,
      domain: "Level 02 — Confidence",
      text: "How many genuinely high-stakes rooms have you been in this quarter?",
      options: [
        { text: "None I can name.", value: "c" },
        { text: "One or two.", value: "f" },
        { text: "Most weeks. It is the job.", value: "p" },
        { text: "I have engineered my role so this rarely happens.", value: "c" }
      ]
    },
    {
      id: 3,
      domain: "Level 03 — Composure",
      text: "In the hardest moment of your last twelve months, what happened to your thinking?",
      options: [
        { text: "It narrowed. I defaulted to the safest available option.", value: "c" },
        { text: "It held, but only just, and it cost me for days afterwards.", value: "f" },
        { text: "It stayed clear. I could still see the angles.", value: "p" },
        { text: "I went quiet and everyone read it as calm. It was not.", value: "f" }
      ]
    },
    {
      id: 4,
      domain: "The plateau",
      text: "Complete the sentence: my performance ceiling is where it is because…",
      options: [
        { text: "I still have things to learn.", value: "c" },
        { text: "I have not put myself in enough situations that could fail.", value: "f" },
        { text: "I lose access to my best thinking exactly when I need it.", value: "p" },
        { text: "The system around me is not built for it.", value: "p" }
      ]
    },
    {
      id: 5,
      domain: "Reading the gap",
      text: "By 4pm on a demanding day, what is actually depleted?",
      options: [
        { text: "My body. I am running on stimulants and momentum.", value: "b" },
        { text: "My judgement. The decisions get worse and I know it.", value: "b" },
        { text: "Nothing much. I finish the day roughly as I started it.", value: "s" },
        { text: "My patience. The work is fine; the people cost more.", value: "m" }
      ]
    },
    {
      id: 6,
      domain: "Reading the gap",
      text: "When your effort stops producing results, what do you do?",
      options: [
        { text: "Add hours. More input eventually breaks it open.", value: "b" },
        { text: "Change the approach. Same effort, different direction.", value: "s" },
        { text: "Question whether I am in the right room at all.", value: "s" },
        { text: "Nothing changes externally. I decide I am not built for it.", value: "m" }
      ]
    },
    {
      id: 7,
      domain: "Reading the gap",
      text: "In the room where it matters most, which is truest?",
      options: [
        { text: "I do not have the physical capacity to sustain that level.", value: "b" },
        { text: "I am playing the wrong game well.", value: "s" },
        { text: "I have everything they have and I still do not feel I belong.", value: "m" },
        { text: "I belong. I just cannot access my best self in there.", value: "m" }
      ]
    },
    {
      id: 8,
      domain: "Physical",
      text: "Your body over the last seven days.",
      options: [
        { text: "Steady. Rested, trained, breathing well.", value: 1, noise: "physical" },
        { text: "Mostly fine. One or two rough nights.", value: 2, noise: "physical" },
        { text: "Under-slept and carrying tension I have stopped noticing.", value: 4, noise: "physical" },
        { text: "Running on empty and pretending otherwise.", value: 5, noise: "physical" }
      ]
    },
    {
      id: 9,
      domain: "Mental",
      text: "How many decisions are open in your head right now?",
      options: [
        { text: "Almost none. Things get closed.", value: 1, noise: "mental" },
        { text: "A few, and I know what they are.", value: 2, noise: "mental" },
        { text: "More than I can list without writing them down.", value: 4, noise: "mental" },
        { text: "I could not tell you. That is the problem.", value: 5, noise: "mental" }
      ]
    },
    {
      id: 10,
      domain: "Emotional",
      text: "Is there something unresolved that follows you into rooms?",
      options: [
        { text: "No. I arrive clean.", value: 1, noise: "emotional" },
        { text: "Something small, and I can set it down.", value: 2, noise: "emotional" },
        { text: "Yes, and it changes how I show up.", value: 4, noise: "emotional" },
        { text: "Yes, and I have been carrying it for a long time.", value: 5, noise: "emotional" }
      ]
    },
    {
      id: 11,
      domain: "Relational",
      text: "How much of your bandwidth goes to managing one difficult dynamic?",
      options: [
        { text: "Almost none.", value: 1, noise: "relational" },
        { text: "Some, and it is proportionate.", value: 2, noise: "relational" },
        { text: "A meaningful amount. It is in meetings they are not even in.", value: 4, noise: "relational" },
        { text: "More than any actual work I did this week.", value: 5, noise: "relational" }
      ]
    },
    {
      id: 12,
      domain: "Environmental",
      text: "How long can you work before something interrupts you?",
      options: [
        { text: "An hour or more, reliably.", value: 1, noise: "environmental" },
        { text: "Thirty to sixty minutes.", value: 2, noise: "environmental" },
        { text: "Ten to twenty minutes.", value: 4, noise: "environmental" },
        { text: "I cannot remember the last uninterrupted hour.", value: 5, noise: "environmental" }
      ]
    }
  ];

  const calculateResult = () => {
    // Count level answers (questions 1-4)
    let levelCounts = { c: 0, f: 0, p: 0 };
    for (let i = 1; i <= 4; i++) {
      const ans = answers[i];
      if (ans && typeof ans === 'string') {
        levelCounts[ans]++;
      }
    }

    // Determine level
    let level;
    if (levelCounts.p >= 2) level = 'p';
    else if (levelCounts.f >= 2) level = 'f';
    else if (levelCounts.c >= levelCounts.f && levelCounts.c >= levelCounts.p) level = 'c';
    else if (levelCounts.f >= levelCounts.p) level = 'f';
    else level = 'p';

    // Count gap answers (questions 5-7)
    let gapCounts = { b: 0, s: 0, m: 0 };
    for (let i = 5; i <= 7; i++) {
      const ans = answers[i];
      if (ans && typeof ans === 'string') {
        gapCounts[ans]++;
      }
    }

    // Determine gap (priority: b > s > m)
    let gap;
    if (gapCounts.b >= gapCounts.s && gapCounts.b >= gapCounts.m) gap = 'b';
    else if (gapCounts.s >= gapCounts.m) gap = 's';
    else gap = 'm';

    // Calculate noise scores (questions 8-12)
    let noiseScores = { physical: 0, mental: 0, emotional: 0, relational: 0, environmental: 0 };
    for (let i = 8; i <= 12; i++) {
      const ans = answers[i];
      if (ans && typeof ans === 'object') {
        noiseScores[ans.noise] = Math.max(noiseScores[ans.noise], ans.value);
      }
    }

    // Find loudest noise
    const noiseOrder = ['physical', 'mental', 'emotional', 'relational', 'environmental'];
    let loudestNoise = 'physical';
    let maxNoiseScore = 0;
    for (const n of noiseOrder) {
      if (noiseScores[n] > maxNoiseScore) {
        maxNoiseScore = noiseScores[n];
        loudestNoise = n;
      }
    }
    const noiseClear = maxNoiseScore <= 2;

    return { level, gap, loudestNoise, noiseClear, noiseScore: maxNoiseScore };
  };

  const handleAnswer = (answer) => {
    setAnswers({ ...answers, [questions[currentQuestion].id]: answer });
    
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      setStage('gate');
    }
  };

  const handleGateSubmit = (e) => {
    e.preventDefault();
    const calculatedResult = calculateResult();
    setResult(calculatedResult);
    setStage('result');
    
    // Here you would send the email with results
    // For now, we'll just show the results
  };

  const levelLabels = {
    c: { title: "Competence", desc: "You know more than the room. It has stopped being the thing that separates you." },
    f: { title: "Confidence", desc: "You have done it before. It holds — until the stakes exceed what you have already survived." },
    p: { title: "Composure", desc: "Rare. Now the work is holding it when the system underneath is depleted." }
  };

  const gapLabels = {
    b: { 
      title: "Body", 
      pct: ">4%",
      desc: "Physical capacity is the constraint — and it is the one high performers are least willing to name, because it feels like a fitness problem rather than a leadership one. It is not. Every hour of judgement you have is drawn from a physiological account, and yours is overdrawn.",
      bold: "No cognitive intervention fixes a capacity problem.",
      action: "Protect one sleep window. Seven nights.",
      module: "Module 03 of Performance by Design is built for exactly this gap — the neuroscience of what recovery actually does, and why stillness is the variable that compounds."
    },
    s: { 
      title: "Strategy", 
      pct: "2%",
      desc: "The body is ready. The effort is real. It is aimed wrong. This is the most expensive gap of the three because it is invisible from the inside — everything feels like progress. You are playing a game well that you should probably not be playing.",
      bold: "Effort is not failing. Direction is.",
      action: "Name what you are removing this week.",
      module: "Module 02 of Performance by Design is the filter — the five domains of performance noise, and how to find the one thing that moves everything else."
    },
    m: { 
      title: "Mind", 
      pct: "1%",
      desc: "You have everything the people around you have. The credentials are there, the capacity is there, the strategy is sound. What is missing is the belief that you belong in the room you are already standing in.",
      bold: "The mind is the last unfair advantage.",
      action: "Say the thing you have been rounding down.",
      module: "Module 01 of Performance by Design is the diagnosis — competence, confidence, composure, and why almost everyone stalls at the second."
    }
  };

  const noiseLabels = {
    physical: "The body is signalling and you have stopped registering it.",
    mental: "Open loops are consuming the bandwidth you need for judgement.",
    emotional: "Unresolved static is arriving in rooms before you do.",
    relational: "One dynamic is taxing every meeting, including the ones it is not in.",
    environmental: "Your attention is being fragmented by design, not by accident."
  };

  return (
    <main className="min-h-screen bg-[#0f151c]">
      <AnimatePresence mode="wait">
        {/* Intro Screen */}
        {stage === 'intro' && (
          <motion.div
            key="intro"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex items-center justify-center px-4"
          >
            <div className="max-w-xl text-center">
              <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.25em] mb-8">
                Peak 9 · School of Shayamal
              </p>
              
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#e8dcc7] mb-8">
                The 1% Gap Diagnostic
              </h1>
              
              <p className="text-[#e8dcc7]/70 text-lg mb-10 leading-relaxed">
                Twelve questions. Three minutes. At the end you will know which of three things is actually holding your ceiling in place — <em className="text-[#e8dcc7]">your body, your strategy, or your mind.</em>
              </p>

              <div className="flex justify-center gap-8 mb-10 text-sm">
                <div>
                  <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mb-1">Questions</p>
                  <p className="text-[#e8dcc7]">12</p>
                </div>
                <div>
                  <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mb-1">Time</p>
                  <p className="text-[#e8dcc7]">3 minutes</p>
                </div>
                <div>
                  <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mb-1">Cost</p>
                  <p className="text-[#e8dcc7]">Free</p>
                </div>
              </div>

              <p className="text-[#e8dcc7]/50 text-sm mb-10">
                No account. No card. Your email is asked for once, at the end, to send the full report.
              </p>

              <button
                data-testid="begin-diagnostic-btn"
                onClick={() => setStage('questions')}
                className="btn-primary inline-flex items-center gap-3"
              >
                Begin the diagnostic
                <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        )}

        {/* Questions Screen */}
        {stage === 'questions' && (
          <motion.div
            key="questions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex flex-col"
          >
            {/* Progress bar */}
            <div className="fixed top-0 left-0 right-0 z-50 bg-[#0f151c] pt-20 pb-4 px-4">
              <div className="max-w-xl mx-auto">
                <div className="flex justify-between text-xs text-[#e8dcc7]/40 mb-2">
                  <span>{currentQuestion + 1} of {questions.length}</span>
                  <span>{questions[currentQuestion].domain}</span>
                </div>
                <div className="h-1 bg-[#e8dcc7]/10 rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-[#e17c18]"
                    initial={{ width: 0 }}
                    animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
                  />
                </div>
              </div>
            </div>

            <div className="flex-1 flex items-center justify-center px-4 pt-32 pb-8">
              <div className="max-w-xl w-full">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentQuestion}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -20 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-xl lg:text-2xl text-[#e8dcc7] mb-10 leading-relaxed">
                      {questions[currentQuestion].text}
                    </h2>

                    <div className="space-y-3" data-testid="question-options">
                      {questions[currentQuestion].options.map((option, i) => (
                        <button
                          key={i}
                          data-testid={`question-option-${i}`}
                          onClick={() => handleAnswer(option.noise ? { value: option.value, noise: option.noise } : option.value)}
                          className="w-full text-left p-5 border border-[#e8dcc7]/20 text-[#e8dcc7]/70 hover:border-[#e17c18] hover:text-[#e8dcc7] transition-all group"
                        >
                          <span className="group-hover:text-[#e8dcc7]">{option.text}</span>
                        </button>
                      ))}
                    </div>

                    {currentQuestion > 0 && (
                      <button
                        onClick={() => setCurrentQuestion(currentQuestion - 1)}
                        className="mt-8 text-[#e8dcc7]/40 text-sm flex items-center gap-2 hover:text-[#e8dcc7]/70 transition-colors"
                      >
                        <ArrowLeft size={14} />
                        Previous question
                      </button>
                    )}
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        )}

        {/* Gate Screen (Email capture) */}
        {stage === 'gate' && (
          <motion.div
            key="gate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen flex items-center justify-center px-4"
          >
            <div className="max-w-md w-full">
              <div className="text-center mb-10">
                <CheckCircle className="w-12 h-12 text-[#e17c18] mx-auto mb-6" />
                <h2 className="text-2xl text-[#e8dcc7] mb-4">Your diagnostic is ready.</h2>
                <p className="text-[#e8dcc7]/60">
                  Enter your name and email to see your result and receive the full report.
                </p>
              </div>

              <form onSubmit={handleGateSubmit} className="space-y-4" data-testid="gate-form">
                <div>
                  <input
                    type="text"
                    placeholder="First name"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    required
                    data-testid="gate-firstname-input"
                    className="w-full bg-transparent border border-[#e8dcc7]/20 text-[#e8dcc7] px-4 py-3 focus:border-[#e17c18] focus:outline-none transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    placeholder="you@company.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="gate-email-input"
                    className="w-full bg-transparent border border-[#e8dcc7]/20 text-[#e8dcc7] px-4 py-3 focus:border-[#e17c18] focus:outline-none transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  data-testid="gate-submit-btn"
                  className="btn-primary w-full flex items-center justify-center gap-2"
                >
                  Show me my gap
                  <ArrowRight size={16} />
                </button>
              </form>

              <p className="text-[#e8dcc7]/40 text-xs text-center mt-6">
                Your data stays with Peak 9. No spam. Unsubscribe anytime.
              </p>
            </div>
          </motion.div>
        )}

        {/* Result Screen */}
        {stage === 'result' && result && (
          <motion.div
            key="result"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="min-h-screen py-24 px-4"
            data-testid="result-screen"
          >
            <div className="max-w-2xl mx-auto">
              <p className="text-[#e17c18] text-xs font-['Syne'] uppercase tracking-[0.25em] mb-6 text-center">
                Your diagnostic
              </p>
              
              <h1 className="text-3xl lg:text-4xl font-light text-[#e8dcc7] mb-4 text-center">
                {formData.firstName}, your gap is <span className="text-[#e17c18]">{gapLabels[result.gap].title}</span>
              </h1>

              <p className="text-5xl text-[#e17c18] font-['Cormorant_Garamond'] text-center mb-12">
                {gapLabels[result.gap].pct}
              </p>

              {/* Gap Description */}
              <div className="bg-[#1a2330] border border-[#e8dcc7]/10 p-8 mb-8">
                <p className="text-[#e8dcc7]/70 leading-relaxed mb-4">
                  {gapLabels[result.gap].desc}
                </p>
                <p className="text-[#e8dcc7] font-medium">
                  {gapLabels[result.gap].bold}
                </p>
              </div>

              {/* Level */}
              <div className="bg-[#1a2330]/50 border border-[#e8dcc7]/10 p-6 mb-8">
                <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mb-2">Your Level</p>
                <p className="text-[#e8dcc7] font-medium mb-1">{levelLabels[result.level].title}</p>
                <p className="text-[#e8dcc7]/60 text-sm">{levelLabels[result.level].desc}</p>
              </div>

              {/* Noise */}
              <div className="bg-[#1a2330]/50 border border-[#e8dcc7]/10 p-6 mb-8">
                <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mb-2">Loudest Noise</p>
                {result.noiseClear ? (
                  <p className="text-[#e8dcc7]/60 text-sm">Running clear. No domain is dominating. Rare — and worth protecting.</p>
                ) : (
                  <>
                    <p className="text-[#e8dcc7] font-medium capitalize mb-1">{result.loudestNoise}</p>
                    <p className="text-[#e8dcc7]/60 text-sm">{noiseLabels[result.loudestNoise]}</p>
                  </>
                )}
              </div>

              {/* 72-hour Action */}
              <div className="border-l-4 border-[#e17c18] pl-6 py-4 mb-12">
                <p className="text-[#e8dcc7]/40 text-xs uppercase tracking-wider mb-2">Your 72-hour Action</p>
                <p className="text-[#e8dcc7] text-lg">{gapLabels[result.gap].action}</p>
              </div>

              {/* Course CTA */}
              <div className="bg-[#e17c18]/10 border border-[#e17c18]/30 p-8 text-center">
                <p className="text-[#e8dcc7]/70 mb-6">
                  {gapLabels[result.gap].module}
                </p>
                <a
                  href="/course"
                  className="btn-primary inline-flex items-center gap-2"
                >
                  See Performance by Design
                  <ArrowRight size={16} />
                </a>
              </div>

              <p className="text-[#e8dcc7]/40 text-sm text-center mt-8">
                Or do nothing with this. The diagnosis is yours either way — that was the deal.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}
